import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box } from '@mui/material';
import Link from '@mui/material/Link';
import { LexicalEditor } from 'lexical';

import { CasRHFLexicalRichTextEditor, CasRHFTextField } from 'src/compass/shared/components';
import { IDataTableConfig } from 'src/compass/shared/components/CasLexicalRichTextEditor/CasPlugins/CasTableDataPlugin/CasTableDataNode';
// Direct paths, not the application barrel: this file is inside it, and importing it from
// itself is a cycle (see CasPendingEmailsPill in CasContextProviders).
import { RHFMessageRecipients } from './RHFMessageRecipients';
import RHFMessageCategory from './RHFMessageCategory';
import SmsImageList from './SmsImageList';
import SmsTextField from './SmsTextField';
import RingCentralConnectAlert from './ringCentral/RingCentralConnectAlert';
import RingCentralFromNumber from './ringCentral/RingCentralFromNumber';
import { SmsTextAdjustmentType } from './smsSpamSafeText';
import {
  RecipientOptionType,
  SendAsType,
  SendChannelType,
  isChannelLocked,
  resolveChannel,
  supportsChannel,
} from './composeMode';

/*
  The fields every compose window shares: the message's Category, recipients, the channel they
  settle, a subject and a body that is rich text on email and plain text on a text message.

  What differs between senders is passed in. SCE Reports composes a report - so it hands in a
  dataTableConfig for the data objects its editor inserts, and smsExtras for the picker that names
  which report a text links to. A customer message is the sender's own words and passes neither,
  which is the whole of the difference between the two windows.
*/
interface Props {
  /* The route serving the To/CC pick list, and what identifies whose list it is. */
  optionsEntity: string;
  optionsParams: Record<string, string | number | undefined>;
  selectedTo: RecipientOptionType[];
  selectedCC: RecipientOptionType[];
  update: Date | null;
  dataTableConfig?: IDataTableConfig;
  onReadyEditor?: Dispatch<SetStateAction<LexicalEditor | null>>;
  onChannelChange: (channel: SendChannelType) => void;
  onClearBody?: () => void;
  onEditSignature: () => void;
  /*
    Whether the seeded defaults are re-resolved when the channel is pinned - i.e. whether the
    window refetches them addressed on the new channel. When they are, the prune below leaves them
    alone: they are about to be replaced by the same contacts' other address, and dropping them
    reported people as unreachable who are reachable perfectly well.
  */
  defaultsFollowChannel?: boolean;
  smsAdjustments: SmsTextAdjustmentType[];
  onDismissSmsAdjustments: () => void;
  /* Rendered under the text body - where SCE Reports puts its data object picker. */
  smsExtras?: ReactNode;
}

export default function MessageComposeFields({
  optionsEntity,
  optionsParams,
  selectedTo,
  selectedCC,
  update,
  dataTableConfig,
  onReadyEditor,
  onChannelChange,
  onClearBody,
  onEditSignature,
  defaultsFollowChannel,
  smsAdjustments,
  onDismissSmsAdjustments,
  smsExtras,
}: Props) {
  const { control, getValues, setValue } = useFormContext();
  const [dropped, setDropped] = useState<string[]>([]);

  /*
    No defaultValue override here. The recipients are seeded from the form's own
    defaultValues, and passing one would make these watches start on that value
    instead - which the seeding setValue in RHFMessageRecipients cannot correct,
    since a child's effect runs before this component has subscribed.
  */
  const sendAs: SendAsType = useWatch({ control, name: 'sendAs' }) ?? 'auto';
  const to: RecipientOptionType[] = useWatch({ control, name: 'to' }) ?? [];
  const cc: RecipientOptionType[] = useWatch({ control, name: 'cc' }) ?? [];
  // The body's pictures, as the window put them in the field when the text was derived.
  const smsImages: string[] = useWatch({ control, name: 'smsImages' }) ?? [];

  const channel = resolveChannel(sendAs, to, cc);
  const channelLocked = isChannelLocked(sendAs, to, cc);
  const isSms = channel === 'sms';

  useEffect(() => {
    onChannelChange(channel);
  }, [channel, onChannelChange]);

  /*
    Pinning a channel drops the recipients that cannot be reached on it, rather
    than leaving a selection behind that would fail on send. Driven off the value
    rather than a change handler because the Send As control sits in the dialog
    title, outside this form's provider.
  */
  useEffect(() => {
    if (sendAs !== 'email' && sendAs !== 'sms') {
      setDropped([]);
      return;
    }

    const removed: string[] = [];

    /*
      A default recipient carries exactly ONE address - the one its contact prefers (see
      SceRecipientService.DefaultSelection) - so under the other channel it looks unreachable even
      when that contact has a perfectly good number. A window that re-resolves its defaults on the
      pinned channel replaces these a moment later, so they are left where they are rather than
      dropped and reported.
    */
    const defaults = new Set(
      defaultsFollowChannel
        ? [...(selectedTo ?? []), ...(selectedCC ?? [])].map((option) => String(option.id))
        : []
    );

    (['to', 'cc'] as const).forEach((field) => {
      const current: RecipientOptionType[] = getValues(field) ?? [];
      const kept = current.filter((option) => {
        if (supportsChannel(option, sendAs)) return true;
        if (defaults.has(String(option.id))) return true;
        removed.push(option.name);
        return false;
      });

      if (kept.length !== current.length) setValue(field, kept, { shouldValidate: true });
    });

    setDropped(removed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendAs]);

  /*
    That banner only reports something already done, so it clears itself rather than
    sitting there needing a dismissal. A later prune replaces the list and restarts
    the clock.
  */
  useEffect(() => {
    if (!dropped.length) return undefined;

    const timer = setTimeout(() => setDropped([]), 30000);

    return () => clearTimeout(timer);
  }, [dropped]);

  return (
    <>
      {/* Top of the window: a text needs the sender's own RingCentral line connected. */}
      <RingCentralConnectAlert sx={{ mb: 1 }} />

      <RHFMessageCategory />

      {!!dropped.length && (
        <Alert severity="warning" onClose={() => setDropped([])} sx={{ mb: 1 }}>
          {`Removed ${dropped.length} recipient${dropped.length > 1 ? 's' : ''} that cannot receive ${
            channel === 'email' ? 'email' : 'a text'
          }: ${dropped.join(', ')}.`}
        </Alert>
      )}

      {/*
        A text has no CC of its own - everyone named ends up in the same thread - so it
        is shown only while it still holds someone carried over from Email. Emptying it
        in SMS therefore takes it off screen; switching back to Email brings it back.
      */}
      <RHFMessageRecipients
        showCC={!isSms || cc.length > 0}
        optionsEntity={optionsEntity}
        optionsParams={optionsParams}
        selectedTo={selectedTo}
        selectedCC={selectedCC}
        update={update}
        channel={channel}
        channelLocked={channelLocked}
      />

      {/* Subject does not apply to a text message. */}
      {!isSms && <CasRHFTextField name="subject" label="Subject" />}

      {/*
        The rich text body stays mounted so its content - and the data object the
        editor inserts when it first loads - survive a trip through SMS mode.
      */}
      <Box sx={{ display: isSms ? 'none' : undefined }}>
        <CasRHFLexicalRichTextEditor
          mode="advanced"
          name="content"
          label="Body"
          helperText={
            <>
              Your{' '}
              <Link
                component="button"
                // The helper text sits inside the compose form, where a bare <button> would
                // default to type="submit" and send the email.
                type="button"
                underline="always"
                onClick={onEditSignature}
                // A <button> is vertical-align: middle and carries its own font metrics, which
                // lifts it off the sentence's baseline. Inherit both so it sits inline as text.
                sx={{ verticalAlign: 'baseline', font: 'inherit', letterSpacing: 'inherit' }}
              >
                signature
              </Link>{' '}
              will be appended to the Body
            </>
          }
          dataTableConfig={dataTableConfig}
          onReadyEditor={onReadyEditor}
          onClear={onClearBody}
        />
      </Box>

      {isSms && (
        <>
          {!!smsAdjustments.length && (
            <Alert severity="info" onClose={onDismissSmsAdjustments}>
              Some wording was adjusted so carriers are less likely to treat this as spam. Review
              the message and press Send again.
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {smsAdjustments.map((adjustment) => (
                  <li key={`${adjustment.from}->${adjustment.to}`}>
                    &quot;{adjustment.from}&quot; &rarr; &quot;{adjustment.to}&quot;
                  </li>
                ))}
              </Box>
            </Alert>
          )}
          {/* A picture pasted or dropped here joins the ones taken from the body, below. */}
          <SmsTextField picturesName="smsImages" />
          <RingCentralFromNumber sx={{ mt: -1, mb: 1 }} />
          <SmsImageList
            sources={smsImages}
            onRemove={(src) =>
              setValue(
                'smsImages',
                smsImages.filter((image) => image !== src),
                { shouldValidate: true }
              )
            }
          />
          {smsExtras}
        </>
      )}
    </>
  );
}
